import { LoginCredentials, RegisterData, User } from '../types';
import { baseApi } from './baseApi';

export interface ApiResponse<T> {
  success: boolean;
  response: T;
  message?: string;
}

export interface LoginResponse {
  user?: User;
  token?: string;
  accessToken?: string;
  refreshToken?: string;
  requiresTwoFactor?: boolean;
  preAuthToken?: string;
  expiresInSeconds?: number;
  methods?: {
    authApp: boolean;
    emailOtp: boolean;
  };
  isEmailVerified?: boolean;
  accountStatus?: string;
}

export interface Complete2FAPayload {
  preAuthToken: string;
  authAppToken?: string;
  emailOtp?: string;
}

export interface AuthUserRolePermission {
  id: string;
  tenantId: string;
  action: string;
  key: string;
  resource: string;
  createdAt: string;
  updatedAt: string;
}

export interface AuthUserStaffRole {
  id: string;
  tenantId: string;
  key: string;
  name: string;
  description: string;
  canBeScoped: boolean;
  icon: string | null;
  createdAt: string;
  updatedAt: string;
  rolePermissions: AuthUserRolePermission[];
}

export interface AuthUserStaffRoleAssignment {
  id: string;
  tenantId: string;
  staffProfileId: string;
  staffRoleId: string;
  assignedBy: string;
  assignmentReason: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  staffRole: AuthUserStaffRole;
}

export interface AuthUserStaffProfile {
  id: string;
  tenantId: string;
  userId: string;
  staffId: string;
  employeeId: string | null;
  hireDate: string;
  departmentId: string | null;
  staffInvitationId: string | null;
  invitationStatus: string | null;
  jobTitle: string | null;
  managerId: string | null;
  metadata: {
    alternateEmailVerified: boolean;
    smsNotifications: boolean;
    pushNotifications: boolean;
    emailNotifications: boolean;
  };
  contactInfo: AuthUserContactInfo;
  createdAt: string;
  updatedAt: string;
  department: any | null;
  staffRoleAssignments: AuthUserStaffRoleAssignment[];
}

export interface AuthUserAvatarFile {
  id: string;
  tenantId: string;
  fileName: string;
  publicId: string;
  url: string;
  secureUrl: string;
  fileType: string;
  status: string;
  size: string;
  width: number;
  height: number;
  uploadedBy: string;
  folder: string;
  metadata: any;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface AuthUserContactInfo {
  phoneNumber?: string;
  alternateEmail?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelationship?: string;
}

export interface AuthUserProfile {
  id: string;
  tenantId: string;
  email: string;
  alternateEmail: string | null;
  phoneNumber: string | null;
  firstName: string;
  lastName: string;
  displayName: string;
  middleName: string | null;
  title: string | null;
  bio: string | null;
  accountStatus: string;
  accountType: string;
  googleId: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  avatarFileId: string | null;
  contactInfo: AuthUserContactInfo | null;
  metadata: {
    loginCount: number;
    lastLoginAt: string;
    emailVerifiedAt: string;
    isEmailVerified: boolean;
    alternateEmailVerified: boolean;
    smsNotifications: boolean;
    pushNotifications: boolean;
    emailNotifications: boolean;
  };
  createdAt: string;
  updatedAt: string;
  avatarFile?: AuthUserAvatarFile;
  staffProfile?: AuthUserStaffProfile;
}

export interface UpdateProfileContactPayload {
  phoneNumber?: string;
  alternateEmail?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelationship?: string;
}

export interface UpdateAuthUserProfilePayload {
  firstName?: string;
  lastName?: string;
  middleName?: string | null;
  dateOfBirth?: string | null;
  gender?: string | null;
  alternateEmail?: string | null;
  bio?: string | null;
  avatarFileId?: string | null;
}

export interface VerifyAlternateEmailRequest {
  token: string;
  email?: string;
  userId?: string;
}

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<
      ApiResponse<LoginResponse>,
      LoginCredentials
    >({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: { ...credentials, portal: 'admin' },
      }),
    }),
    send2FAEmail: builder.mutation<ApiResponse<null>, { preAuthToken: string }>({
      query: (data) => ({
        url: '/auth/2fa/email/send',
        method: 'POST',
        body: data,
      }),
    }),
    complete2FA: builder.mutation<ApiResponse<LoginResponse>, Complete2FAPayload>({
      query: (data) => ({
        url: '/auth/2fa/complete',
        method: 'POST',
        body: data,
      }),
    }),
    register: builder.mutation<
      ApiResponse<{ user: User; token: string }>,
      RegisterData
    >({
      query: (userData) => ({
        url: '/auth/register',
        method: 'POST',
        body: userData,
      }),
    }),
    logout: builder.mutation<ApiResponse<null>, { refreshToken: string }>({
      query: (data) => ({
        url: '/auth/logout',
        method: 'POST',
        body: data,
      }),
    }),
    forgotPassword: builder.mutation<ApiResponse<null>, { email: string }>({
      query: (data) => ({
        url: '/auth/forgot-password',
        method: 'POST',
        body: data,
      }),
    }),
    resetPassword: builder.mutation<
      ApiResponse<null>,
      { email: string; token: string; password: string }
    >({
      query: (data) => ({
        url: '/auth/reset-password',
        method: 'POST',
        body: data,
      }),
    }),
    verifyEmail: builder.mutation<ApiResponse<null>, { token: string; email: string }>({
      query: (data) => ({
        url: '/auth/verify-email',
        method: 'POST',
        body: data,
      }),
    }),
    resendVerification: builder.mutation<ApiResponse<null>, { email: string }>({
      query: (data) => ({
        url: '/auth/send-verification-email',
        method: 'POST',
        body: data,
      }),
    }),
    verifyInvitation: builder.mutation<ApiResponse<{ email: string; firstName: string; lastName: string }>, { token: string }>({
      query: (data) => ({
        url: '/staff/invitations/verify',
        method: 'POST',
        body: data,
      }),
    }),
    acceptInvitation: builder.mutation<ApiResponse<null>, { token: string; password: string }>({
      query: (data) => ({
        url: '/staff/invitations/accept',
        method: 'POST',
        body: data,
      }),
    }),
    getAuthUserProfile: builder.query<ApiResponse<AuthUserProfile>, void>({
      query: () => '/auth/profile',
      providesTags: ['User'],
    }),
    updateAuthUserProfileContact: builder.mutation<ApiResponse<AuthUserProfile>, UpdateProfileContactPayload>({
      query: (data) => ({
        url: '/auth/profile/contact',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),
    updateAuthUserProfile: builder.mutation<ApiResponse<AuthUserProfile>, UpdateAuthUserProfilePayload>({
      query: (data) => ({
        url: '/auth/profile',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),
    sendAlternateEmailVerification: builder.mutation<ApiResponse<null>, { email: string }>({
      query: (data) => ({
        url: '/auth/send-alternate-email-verification',
        method: 'POST',
        body: data,
      }),
    }),
    verifyAlternateEmail: builder.mutation<ApiResponse<null>, VerifyAlternateEmailRequest>({
      query: (data) => ({
        url: '/auth/verify-alternate-email',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useLogoutMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useVerifyEmailMutation,
  useResendVerificationMutation,
  useVerifyInvitationMutation,
  useAcceptInvitationMutation,
  useGetAuthUserProfileQuery,
  useUpdateAuthUserProfileContactMutation,
  useUpdateAuthUserProfileMutation,
  useSend2FAEmailMutation,
  useComplete2FAMutation,
  useSendAlternateEmailVerificationMutation,
  useVerifyAlternateEmailMutation,
} = authApi;